import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateSolarLightReachReaders } from "./solar-price-dependents"
import type { SolarProposalSavingsSectionDto } from "../dto/solar-proposal-savings.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export type RecalculateSolarProposalSavingsVariables = {
  proposalId: string
}

/**
 * Recalculate savings for a Solar Proposal. The answer is the savings read
 * itself, so it replaces that cache entry. The run rewrites the usage estimate
 * (and the default contracted rate the utility read shows) and the Brochure,
 * whose production and offset the header, design and impact reads show, so
 * those refetch, as does contract readiness; pricing is never touched.
 */
export default function useRecalculateSolarProposalSavings(): UseMutationResult<
  SolarProposalSavingsSectionDto,
  unknown,
  RecalculateSolarProposalSavingsVariables
> {
  const queryClient = useQueryClient()

  const refetchRecalculatedReads = (proposalId: string): void => {
    for (const queryKey of [
      queryKeys.solarProposal.byId(proposalId),
      queryKeys.solarProposal.design(proposalId),
      queryKeys.solarProposal.impact(proposalId),
      queryKeys.solarProposal.utility(proposalId),
      // The contract's savings freshness blockers clear or remain.
      queryKeys.solarProposal.contract(proposalId),
      queryKeys.solarProposal.share(proposalId),
    ]) {
      void queryClient.invalidateQueries({ queryKey, exact: true })
    }
    // Get LightReach pricing waits on current savings.
    invalidateSolarLightReachReaders(queryClient, proposalId)
  }

  return useMutation({
    mutationFn: ({ proposalId }: RecalculateSolarProposalSavingsVariables) =>
      solarProposalService.postSavings(proposalId),
    onSuccess: (savings, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.solarProposal.savings(proposalId),
        savings,
      )
      refetchRecalculatedReads(proposalId)
    },
    // A superseded or failed run can still have committed its estimate or
    // Brochure before answering, and its status may have moved (a save can
    // restore the inputs the stored figures were built from).
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.savings(proposalId),
        exact: true,
      })
      refetchRecalculatedReads(proposalId)
    },
  })
}
